import { useMemo } from "react";
import { z } from "zod";
import { getWebApp } from "./webApp";

const userSchema = z
  .object({
    id: z.number(),
    is_bot: z.boolean().optional(),
    first_name: z.string(),
    last_name: z.string().optional(),
    username: z.string().optional(),
    language_code: z.string().optional(),
    is_premium: z.boolean().optional(),
    photo_url: z.string().optional(),
    allows_write_to_pm: z.boolean().optional(),
    added_to_attachment_menu: z.boolean().optional(),
  })
  .passthrough();

const initDataSchema = z
  .object({
    user: userSchema.optional(),
    query_id: z.string().optional(),
    auth_date: z.number(),
    hash: z.string(),
    start_param: z.string().optional(),
  })
  .passthrough();

export type TelegramInitDataUser = z.infer<typeof userSchema>;

export interface TelegramInitData {
  user: TelegramInitDataUser | null;
  queryId: string | null;
  authDate: number;
  hash: string;
  startParam: string | null;
  /** Raw signed payload — pass to `POST /api/auth/session` for HMAC verification. */
  raw: string;
}

/**
 * Parse and validate Telegram's `initData` payload.
 *
 * Returns `null` outside a real Telegram client (desktop preview, Vitest with
 * no SDK stub). Memoised on mount because Telegram does not rotate `initData`
 * within a session — re-parsing on every render would just burn cycles and
 * break referential equality for downstream consumers.
 *
 * The shape is strict: validation failures throw at parse time so a malformed
 * payload surfaces in Sentry instead of silently propagating `undefined` into
 * the auth request body.
 */
export function useInitData(): TelegramInitData | null {
  return useMemo(() => {
    const sdk = getWebApp();
    if (!sdk) return null;
    const parsed = initDataSchema.parse(sdk.initDataUnsafe);
    return {
      user: parsed.user ?? null,
      queryId: parsed.query_id ?? null,
      authDate: parsed.auth_date,
      hash: parsed.hash,
      startParam: parsed.start_param ?? null,
      raw: sdk.initData,
    };
  }, []);
}
